import { useEffect } from 'react'
import get from 'lodash/get'

import { useGetDataFromColumnsObject } from '@components/TimeLineGrid/hooks/useGetDataFromColumnsObject'

import { useExecuteCommandMutation } from '@redux/api'

import { ListQueryResponse, useInfiniteScroll } from '@hooks'
import { getCommandRequestData } from '@helpers'
import {
  ACTION_CODE,
  EVENT_CODE,
  TIME_LINE_INFINITE_SCROLL_Y_PAGINATION_SIZE,
  TIME_LINE_MODE,
} from '@constants'
import {
  ExecuteCommandPayload,
  GETVariableDTO,
  MainObjectTypeEnum,
  ObjectDataRecord,
  TimeLineDTOType,
  UseMutationType,
} from '@types'

import useModifyDataAfterLoad from '../../../hooks/actions/useModifyDataAfterLoad'
import {
  convertApiResponseToTimeLineGridData,
  getFilteredColumnsObjectDataByObjectXData,
  getKeyFieldY,
  getObjectYCommandParams,
} from '../helpers'

import { useGetDataFromObjectXCommand } from './useGetDataFromObjectXCommand'
import { useGetDataFromObjectYCommand } from './useGetDataFromObjectYCommand'
import { RefreshTotalsParams } from './useGetTotals'
import { useHandlers } from './useHandlers'

type UseObjectYDataParams = {
  config: TimeLineDTOType
  refreshTotals: (params: RefreshTotalsParams) => void
} & ReturnType<typeof useHandlers>

export const useGetDataByMainObjectY = ({
  config,
  refreshTotals,
  handlers,
  state,
  data,
}: UseObjectYDataParams) => {
  const {
    handleChangeIsLoadingTimeLineGridData,
    handleChangeIsLoadingColumnsObjectData,
    handleChangeTimeLineGridData,
    handleChangeVariables,
    handleExecuteActions,
    handleChangeColumnsObjectData,
  } = handlers
  const {
    dateIntervals,
    sortModel,
    objectXSARSQLFilter,
    objectYSARSQLFilter,
    objectXColumnsObjectSARSQLFilter,
    objectYSAFiltersCriteria,
    objectXColumnsObjectSAFiltersCriteria,
    objectXSAFiltersCriteria,
    isUseMainObjectXBySAFilter,
    isFilterColumnsObjectByObjectXFilter,
  } = state
  const { startDate, endDate, objectYActionsObject, isNeedWaitFirstLoadWithSA, objectXDataRef } =
    data

  const { modifyData } = useModifyDataAfterLoad()

  const loadDataByMainObjectY =
    (!config.mainObject?.objectType || config.mainObject?.objectType === MainObjectTypeEnum.Y) &&
    !isUseMainObjectXBySAFilter
  const isLoadDataWithGrouping = Boolean(config.mainObject?.groupField?.length)

  const { getDataFromObjectXCommand } = useGetDataFromObjectXCommand()
  const { getDataFromObjectYCommand, totalCountElements: yCommandTotalCountElement } =
    useGetDataFromObjectYCommand()
  const { getDataFromColumnsObject } = useGetDataFromColumnsObject()

  const {
    readMore,
    totalPages,
    isError,
    totalCountElements: infiniteScrollTotalCountElements,
    refresh: infiniteScrollRefresh,
  } = useInfiniteScroll<ExecuteCommandPayload, ObjectDataRecord>(
    useExecuteCommandMutation as UseMutationType<
      ExecuteCommandPayload,
      ListQueryResponse<ObjectDataRecord>
    >,
    {
      transformResponseWithPagination: true,
      objectCode: config.objectY.objectCode,
      onInitialLoad,
      commandCode: config.objectY.getDataCommand?.name as string,
      size: TIME_LINE_INFINITE_SCROLL_Y_PAGINATION_SIZE,
      method: config.objectY.getDataCommand?.httpMethod,
      params: getObjectYCommandParams({
        config,
        commandParameters: config.objectY.getDataCommand?.parameters,
        saRSQLFilter: objectXSARSQLFilter,
        sort: sortModel,
      }),
      body:
        config.objectY.getDataCommand?.body && config.objectY.getDataCommand?.httpMethod !== 'GET'
          ? getCommandRequestData(config.objectY.getDataCommand.body, {})
          : undefined,
    },
    undefined,
    undefined,
    undefined,
    false
  )

  const getObjectXDataByObjectYData = async (
    objectYData: ObjectDataRecord[],
    isInitialLoad?: boolean
  ) => {
    const keyFieldY = getKeyFieldY(config)

    if (!objectYData.length) {
      handleChangeIsLoadingTimeLineGridData(false)

      convertApiResponseToTimeLineGridData({
        config,
        dateIntervals,
        objectXData: [],
        objectYData: [],
        keyFieldY,
        handleChangeColumnsObjectData,
        handleChangeTimeLineGridData,
        isInitialLoad: Boolean(isInitialLoad),
        columnsObjectData: [],
        objectXSAFiltersCriteria,
      })

      return
    }

    const data = await modifyData(objectYActionsObject, objectYData)

    // Достаём из ObjectY значения по ключевым полям
    const objectYKeyFieldsValues = data.map((item: Record<typeof keyFieldY, unknown>) =>
      get(item, keyFieldY)
    )

    const promises = [
      getDataFromObjectXCommand({
        config,
        startDate,
        endDate,
        objectYKeyFieldsValues,
        objectXSARSQLFilter,
        objectYSAFiltersCriteria,
        objectXColumnsObjectSAFiltersCriteria,
      }),
    ]

    if (config.mode === TIME_LINE_MODE.OBJECT) {
      handleChangeIsLoadingColumnsObjectData(true)
      promises.push(
        getDataFromColumnsObject({ config, rsqlFilter: objectXColumnsObjectSARSQLFilter })
      )
    }

    Promise.all(promises)
      .then(values => {
        if (config.mode === TIME_LINE_MODE.OBJECT) {
          if (values[1]?.length) {
            const totalsColumnsObjectData = isFilterColumnsObjectByObjectXFilter
              ? getFilteredColumnsObjectDataByObjectXData({
                  config,
                  columnsObjectData: values[1],
                  objectXData: isInitialLoad
                    ? values[0] || []
                    : [...(objectXDataRef.current || []), values[0] || []],
                })
              : values[1]

            refreshTotals({
              columnsObjectData: totalsColumnsObjectData,
              objectYSAFiltersCriteria,
              objectXColumnsObjectSAFiltersCriteria,
              objectXSAFiltersCriteria,
            })
          }

          handleChangeIsLoadingColumnsObjectData(false)
        }

        convertApiResponseToTimeLineGridData({
          config,
          dateIntervals,
          objectXData: values[0],
          objectYData: data,
          keyFieldY,
          handleChangeColumnsObjectData,
          handleChangeTimeLineGridData,
          isInitialLoad: Boolean(isInitialLoad),
          columnsObjectData: values[1],
          objectXSAFiltersCriteria,
        })
      })
      .finally(() => {
        if (config.mode === TIME_LINE_MODE.OBJECT) {
          handleChangeIsLoadingColumnsObjectData(false)
        }
        handleChangeIsLoadingTimeLineGridData(false)
      })

    return
  }

  const getAllData = () => {
    return getDataFromObjectYCommand({
      config,
      sort: sortModel,
      saRSQLFilter: objectYSARSQLFilter,
    }).then(objectYData => {
      getObjectXDataByObjectYData(objectYData, true)
    })
  }

  function onInitialLoad(initialData: ListQueryResponse<ObjectDataRecord>) {
    getObjectXDataByObjectYData(initialData.data, true)
  }

  const refresh =
    loadDataByMainObjectY && isLoadDataWithGrouping ? getAllData : infiniteScrollRefresh

  const totalCountElements =
    loadDataByMainObjectY && isLoadDataWithGrouping
      ? yCommandTotalCountElement
      : infiniteScrollTotalCountElements

  useEffect(() => {
    if (isError) {
      handleChangeIsLoadingTimeLineGridData(false)
    }
  }, [isError])

  const refreshData = async () => {
    let variables: Record<string, GETVariableDTO> = {}

    const actionsData = await handleExecuteActions({
      actions: config.actions,
      srcObj: { startDate, endDate },
      eventCode: EVENT_CODE.BEFORE_LOAD_DATA,
    })

    actionsData.forEach(item => {
      if (item.status === 'fulfilled') {
        if (item.value?.actionCode === ACTION_CODE.SET_VARIABLE_VALUE) {
          variables = { ...variables, ...item.value.value }
        }
      }
    })

    handleChangeVariables(variables)

    handleChangeIsLoadingTimeLineGridData(true)
    refresh({
      params: {
        ...getObjectYCommandParams({
          config,
          commandParameters: config.objectY.getDataCommand?.parameters,
          saRSQLFilter: objectYSARSQLFilter,
          sort: sortModel,
        }),
      },
    })
  }

  useEffect(() => {
    if (!loadDataByMainObjectY || isNeedWaitFirstLoadWithSA) {
      return
    }

    refreshData()
  }, [dateIntervals, sortModel, objectYSAFiltersCriteria])

  const onReadMore = () => {
    handleChangeIsLoadingTimeLineGridData(true)
    readMore()
      .then(newPageData => {
        if (newPageData) {
          getObjectXDataByObjectYData(newPageData.data)

          return
        }

        handleChangeIsLoadingTimeLineGridData(false)
      })
      .catch(() => handleChangeIsLoadingTimeLineGridData(false))
  }

  return {
    onReadMore,
    totalPages,
    totalCountElements,
    refresh,
  }
}
